import React, {useRef, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';

import {withRouter} from 'react-router-dom';
import {Canvas} from './Particles.styles';
import {debounce} from '../../lib/helpers';

const Particles = ({match}) => {
  const path = match.params.layerID;

  const canvas = useRef(null);
  const context = useRef(null);
  const width = useRef(window.innerWidth || 1080);
  const height = useRef(window.innerHeight || 1540);
  const raf = useRef(null);
  const then = useRef();
  const now = useRef();
  const particles = useRef();
  const elapsed = useRef();

  let fps = 15;
  let fpsInterval = useRef(1000 / fps);

  const onResize = debounce(redraw, 250);

  function resize() {
    // Lookup the size the browser is displaying the canvas.
    var displayWidth = canvas.current.clientWidth || window.innerWidth;
    var displayHeight = canvas.current.clientHeight || window.innerHeight;

    // Check if the canvas is not the same size.
    if (
      canvas.current.width !== displayWidth ||
      canvas.current.height !== displayHeight
    ) {
      // Make the canvas the same size
      canvas.current.width = width.current = Math.ceil(displayWidth);
      canvas.current.height = height.current = Math.ceil(displayHeight);
    }
  }

  const createParticles = useCallback(() => {
    const particlesCount = width.current * 100;

    particles.current = Array.from({length: particlesCount}, () => [
      Math.round(Math.random() * (width.current - 1)),
      Math.round(Math.random() * (height.current - 1)),
    ]);
  }, []);

  const animate = useCallback(() => {
    now.current = Date.now();
    elapsed.current = now.current - then.current;

    if (elapsed.current > fpsInterval.current) {
      then.current = now.current - (elapsed.current % fpsInterval.current);
      draw();
      updateParticles();
    }

    raf.current = requestAnimationFrame(animate);
  }, []);

  const init = useCallback(() => {
    resize();
    createParticles();

    then.current = Date.now();
    raf.current = requestAnimationFrame(animate);
  }, [animate, createParticles]);

  function updateParticles() {
    particles.current.forEach((particle) => {
      particle[0] = Math.max(
        0,
        Math.min(
          width.current - 1,
          Math.round(particle[0] + Math.random() * 2 - 1)
        )
      );

      particle[1] = Math.max(
        0,
        Math.min(
          height.current - 1,
          Math.round(particle[1] + Math.random() * 2 - 1)
        )
      );
    });
  }

  useEffect(() => {
    context.current = canvas.current.getContext('2d');
    init();

    window.addEventListener('resize', onResize);

    return function cleanup() {
      window.removeEventListener('resize', onResize);
      cleanupAnimation();
    };
  }, [init, onResize]);

  function draw() {
    const ctx = context.current || canvas.current.getContext('2d');

    ctx.clearRect(0, 0, width.current, height.current);
    const imageData = ctx.getImageData(0, 0, width.current, height.current);
    const data = imageData.data;

    for (let i = 0; i < particles.current.length; i++) {
      const particle = particles.current[i];
      const index = 4 * (particle[0] + particle[1] * width.current);
      data[index + 0] = 255;
      data[index + 1] = 255;
      data[index + 2] = 255;
      data[index + 3] = 255;
    }

    ctx.putImageData(imageData, 0, 0);
  }

  function redraw() {
    resize();
    createParticles();
  }

  function cleanupAnimation() {
    cancelAnimationFrame(raf.current);
    raf.current = null;
    context.current = null;
  }
  return (
    <Canvas
      path={path}
      ref={canvas}
      width={width.current}
      height={height.current}
    />
  );
};

Particles.propTypes = {
  match: PropTypes.object,
};

export default withRouter(Particles);
